// src/components/tour/ProductTour.jsx
//
// Game-style "spotlight" tour that runs on top of the LIVE page.
// It never renders fake data: every step points at a real element
// (via CSS selector, usually [data-tour="..."]) and simply cuts a
// hole in a dark overlay around it, with an explanation card next to it.
//
// Usage:
//   <ProductTour open={tourOpen} steps={steps} onClose={() => setTourOpen(false)} />
//
// Step shape:
//   {
//     target:  '[data-tour="x"]' | ['[data-tour="a"]', '[data-tour="b"]'] | null,
//              // first selector that matches a visible element wins.
//              // null = centered card with no spotlight (intro / outro).
//              // If nothing matches, the step is skipped automatically
//              // (e.g. "first device row" when the list is empty).
//     title:   'Short title',
//     content: 'Text or JSX',
//     onEnter: () => {},   // optional: open a panel etc. before measuring
//     padding: 8,          // optional: spotlight padding in px
//   }
//
// Clicks are blocked while the tour is open, so a user can't
// accidentally press DELETE on a highlighted row.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ProductTour.css';

const MOBILE_MAX = 600;
const GAP = 14;        // space between spotlight and card
const EDGE = 12;       // min distance from viewport edges
const WAIT_MS = 800;   // how long to wait for a target to appear after onEnter

function findTarget(target) {
    if (!target) return null;
    const list = Array.isArray(target) ? target : [target];
    for (const sel of list) {
        // Several elements can match (e.g. a desktop input hidden by CSS on
        // phones plus its mobile icon) - take the first one actually visible.
        for (const el of document.querySelectorAll(sel)) {
            const r = el.getBoundingClientRect();
            if (r.width > 0 && r.height > 0) return el;
        }
    }
    return null;
}

// Scrollable ancestors (e.g. Site Dashboard's .ft-grid-left list box).
function getScrollParents(el) {
    const parents = [];
    let p = el.parentElement;
    while (p && p !== document.body && p !== document.documentElement) {
        const { overflowY } = window.getComputedStyle(p);
        if (/(auto|scroll|overlay)/.test(overflowY) && p.scrollHeight > p.clientHeight + 1) {
            parents.push(p);
        }
        p = p.parentElement;
    }
    return parents;
}

// onBoxScrolled(box) is called before an inner box is scrolled, so the
// tour can put it back where it was when it closes.
function scrollIntoViewIfNeeded(el, onBoxScrolled) {
    // 1) Reveal the element inside any inner scroll box first (instantly, so
    //    the window step below measures the final position). Innermost first.
    for (const box of getScrollParents(el)) {
        const b = box.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        const margin = 16;
        if (r.top < b.top + margin || r.bottom > b.bottom - margin) {
            const tall = r.height > b.height - margin * 2;
            const delta = tall
                ? r.top - b.top - margin
                : (r.top - b.top) - (b.height - r.height) / 2;
            onBoxScrolled && onBoxScrolled(box);
            box.scrollTop += delta;
        }
    }

    // 2) Then the window, leaving room for the card (bottom sheet on phones).
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const topSafe = 80;
    const bottomSafe = window.innerWidth <= MOBILE_MAX ? vh * 0.45 : 180;
    if (r.top >= topSafe && r.bottom <= vh - bottomSafe) return;
    const tall = r.height > vh - topSafe - bottomSafe;
    const delta = tall ? r.top - topSafe : r.top - (vh - bottomSafe - r.height + topSafe) / 2;
    window.scrollBy({ top: delta, behavior: 'smooth' });
}

export default function ProductTour({ open, steps, onClose, startAt = 0 }) {
    const [index, setIndex] = useState(startAt);
    const [rect, setRect] = useState(null);          // spotlight rect (viewport coords)
    const [cardPos, setCardPos] = useState(null);    // {top,left} or {dock:'top'|'bottom'}
    const [ready, setReady] = useState(false);
    const dirRef = useRef(1);                        // 1 = forward, -1 = backward
    const elRef = useRef(null);
    const cardRef = useRef(null);
    const rafRef = useRef(0);
    const scrollRestoreRef = useRef(null); // { windowY, boxes: Map<el, scrollTop> }

    const step = steps[index];
    const padding = step?.padding ?? 8;

    // Reset when (re)opened; remember scroll positions to restore on close.
    useEffect(() => {
        if (open) {
            dirRef.current = 1;
            setIndex(startAt);
            scrollRestoreRef.current = { windowY: window.scrollY, boxes: new Map() };
        } else if (scrollRestoreRef.current) {
            const { windowY, boxes } = scrollRestoreRef.current;
            boxes.forEach((top, box) => { if (box.isConnected) box.scrollTop = top; });
            window.scrollTo({ top: windowY, behavior: 'smooth' });
            scrollRestoreRef.current = null;
        }
    }, [open, startAt]);

    const rememberBox = (box) => {
        const store = scrollRestoreRef.current;
        if (store && !store.boxes.has(box)) store.boxes.set(box, box.scrollTop);
    };

    const close = useCallback(() => {
        elRef.current = null;
        onClose && onClose();
    }, [onClose]);

    const go = useCallback((dir) => {
        dirRef.current = dir;
        const n = index + dir;
        if (n < 0) return;
        if (n >= steps.length) { close(); return; }
        setIndex(n);
    }, [index, steps.length, close]);

    // Resolve the target for the current step (after onEnter), skipping missing ones.
    useEffect(() => {
        if (!open || !step) return;
        let cancelled = false;
        setReady(false);
        step.onEnter && step.onEnter();

        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug(`[tour] step ${index + 1}/${steps.length}: ${step.title}`, step.target || '(no target)');
        }

        if (!step.target) {
            elRef.current = null;
            setRect(null);
            setReady(true);
            return;
        }

        const started = performance.now();
        const tryFind = () => {
            if (cancelled) return;
            const el = findTarget(step.target);
            if (el) {
                elRef.current = el;
                scrollIntoViewIfNeeded(el, rememberBox);
                setReady(true);
            } else if (performance.now() - started < WAIT_MS) {
                requestAnimationFrame(tryFind);
            } else {
                // Target doesn't exist right now (e.g. empty list) -> skip it.
                const next = index + dirRef.current;
                if (next < 0 || next >= steps.length) {
                    if (dirRef.current < 0) setIndex(0); else close();
                } else {
                    setIndex(next);
                }
            }
        };
        tryFind();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index]);

    // Follow the element every frame (handles scrolling, resizing, layout shifts).
    useEffect(() => {
        if (!open || !ready) return;
        const tick = () => {
            const el = elRef.current;
            if (el && el.isConnected) {
                const r = el.getBoundingClientRect();
                setRect((prev) => {
                    const next = {
                        top: r.top - padding,
                        left: r.left - padding,
                        width: r.width + padding * 2,
                        height: r.height + padding * 2,
                    };
                    if (prev && Math.abs(prev.top - next.top) < 0.5 && Math.abs(prev.left - next.left) < 0.5 &&
                        Math.abs(prev.width - next.width) < 0.5 && Math.abs(prev.height - next.height) < 0.5) {
                        return prev;
                    }
                    return next;
                });
            } else if (el && !el.isConnected) {
                // React re-rendered the element (e.g. data refresh) - re-find it.
                elRef.current = findTarget(step?.target);
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [open, ready, padding, step]);

    // Position the explanation card relative to the spotlight.
    useLayoutEffect(() => {
        if (!open || !ready || !cardRef.current) return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const card = cardRef.current.getBoundingClientRect();

        // No target, or the target is still entirely off-screen (e.g. inside a
        // container we couldn't scroll): show the card centred, so the user
        // is never left with a dark screen and nothing to read.
        const offScreen = rect && (rect.top + rect.height < 0 || rect.top > vh ||
            rect.left + rect.width < 0 || rect.left > vw);
        if (!rect || offScreen) { setCardPos({ center: true }); return; }

        if (vw <= MOBILE_MAX) {
            // Phones: dock as a sheet, on the side away from the highlight.
            const mid = rect.top + rect.height / 2;
            setCardPos({ dock: mid > vh * 0.55 ? 'top' : 'bottom' });
            return;
        }

        const clampLeft = (l) => Math.max(EDGE, Math.min(l, vw - card.width - EDGE));
        const clampTop = (t) => Math.max(EDGE, Math.min(t, vh - card.height - EDGE));
        const centerX = rect.left + rect.width / 2 - card.width / 2;
        const centerY = rect.top + rect.height / 2 - card.height / 2;

        if (vh - (rect.top + rect.height) >= card.height + GAP + EDGE) {
            setCardPos({ top: rect.top + rect.height + GAP, left: clampLeft(centerX), side: 'bottom' });
        } else if (rect.top >= card.height + GAP + EDGE) {
            setCardPos({ top: rect.top - card.height - GAP, left: clampLeft(centerX), side: 'top' });
        } else if (vw - (rect.left + rect.width) >= card.width + GAP + EDGE) {
            setCardPos({ top: clampTop(centerY), left: rect.left + rect.width + GAP, side: 'right' });
        } else if (rect.left >= card.width + GAP + EDGE) {
            setCardPos({ top: clampTop(centerY), left: rect.left - card.width - GAP, side: 'left' });
        } else {
            // Huge target: float the card inside it, near the bottom.
            setCardPos({ top: vh - card.height - EDGE * 2, left: clampLeft(centerX), side: 'inside' });
        }
    }, [open, ready, rect, index]);

    // Keyboard: arrows / Enter / Escape.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') close();
            else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); go(1); }
            else if (e.key === 'ArrowLeft') go(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, go, close]);

    if (!open || !step) return null;

    const isLast = index === steps.length - 1;
    const isFirst = index === 0;

    let cardStyle = { visibility: cardPos ? 'visible' : 'hidden' };
    let cardClass = 'pt-card';
    if (cardPos?.center) cardClass += ' pt-card-center';
    else if (cardPos?.dock) cardClass += ` pt-card-dock pt-card-dock-${cardPos.dock}`;
    else if (cardPos) cardStyle = { ...cardStyle, top: cardPos.top, left: cardPos.left };

    return createPortal(
        <div className="pt-root" role="dialog" aria-modal="true" aria-labelledby="pt-title">
            {/* Click shield: blocks interaction with the page during the tour */}
            <div className="pt-shield" onClick={(e) => e.stopPropagation()} />

            {rect ? (
                <div
                    className="pt-spotlight"
                    style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
                />
            ) : (
                <div className="pt-backdrop" />
            )}

            <div ref={cardRef} className={cardClass} style={cardStyle} key={index}>
                <div className="pt-card-head">
                    <span className="pt-step-count">STEP {index + 1} / {steps.length}</span>
                    <button type="button" className="pt-skip" onClick={close}>
                        {isLast ? 'CLOSE' : 'SKIP TOUR'}
                    </button>
                </div>
                <h3 id="pt-title" className="pt-title">{step.title}</h3>
                <div className="pt-content">{step.content}</div>

                <div className="pt-card-foot">
                    {steps.length <= 10 ? (
                        <div className="pt-dots" aria-hidden="true">
                            {steps.map((_, i) => (
                                <span key={i} className={'pt-dot' + (i === index ? ' active' : i < index ? ' done' : '')} />
                            ))}
                        </div>
                    ) : (
                        // Long tours: a slim bar instead of a row of dots that would wrap.
                        <div className="pt-bar" aria-hidden="true">
                            <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
                        </div>
                    )}
                    <div className="pt-nav">
                        <button type="button" className="pt-btn pt-btn-ghost" onClick={() => go(-1)} disabled={isFirst}>
                            BACK
                        </button>
                        <button type="button" className="pt-btn pt-btn-primary" onClick={() => go(1)}>
                            {isLast ? 'DONE' : 'NEXT'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
